import { decide, sharesArtwork, type Candidate } from "@webscan/decide.js";
import { FRAMES_WITH_COLLECTOR_NUMBER } from "@webscan/ocrprep.js";
import {
  collectorNumbersMatch,
  nameSimilarity,
  ocrWasLegible,
  parseCollectorBlock,
} from "@webscan/text.js";
import type { Identification } from "../protocol/events";
import type { CardText } from "./ocr";

/** Identifiers may choose a printing only within the artwork that retrieval selected. */
export function applyCollectorEvidence(candidates: Candidate[], text: CardText): boolean {
  const leader = candidates[0];
  const info = parseCollectorBlock(text.collector.text, text.setInfo.text);
  if (!leader || !ocrWasLegible(text, info)) return false;
  for (const candidate of candidates) {
    if (!sharesArtwork(leader, candidate)) continue;
    if (text.title.text)
      candidate.ocrNameScore = nameSimilarity(text.title.text, candidate.printing.name);
    if (!FRAMES_WITH_COLLECTOR_NUMBER.has(candidate.printing.frame)) continue;
    candidate.collectorMatch = info.collectorNumber
      ? collectorNumbersMatch(info.collectorNumber, candidate.printing.collector)
      : null;
    candidate.setCodeMatch = info.setCode ? info.setCode === candidate.printing.setCode : null;
  }
  return true;
}

export function printingStillOpen(identification: Identification): boolean {
  const [leader, ...rest] = identification.candidates;
  return (
    identification.verdict === "AMBIGUOUS" &&
    !!leader &&
    FRAMES_WITH_COLLECTOR_NUMBER.has(leader.printing.frame) &&
    rest.some((candidate) => sharesArtwork(leader, candidate))
  );
}

/** Each fresh strip is independent evidence; conflicting attempts never accumulate votes. */
export function refineWithText(
  identification: Identification,
  text: CardText,
): Identification | null {
  if (!printingStillOpen(identification)) return null;
  const candidates = identification.candidates.map((candidate) => ({
    ...candidate,
    collectorMatch: null,
    setCodeMatch: null,
  }));
  if (!applyCollectorEvidence(candidates, text)) return null;
  const decision = decide(candidates, true, identification.verified);
  // A failed strip cannot erase a logged card. The original candidates remain available
  // to the next attempt and to manual review; only confirmed improvement is published.
  if (
    decision.verdict !== "ACCEPTED" ||
    !sharesArtwork(identification.candidates[0], candidates[0])
  )
    return null;
  return { ...identification, ...decision, printing: candidates[0].printing };
}
